import React, { useState } from 'react';
import { AccountLiveTrade } from '../function/type';
import { TradeDetailsCard } from './LiveTradeDetail';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

interface OpenTradesTableProps {
  trades?: AccountLiveTrade[];
  loading?: boolean;
}

const OpenTradesTable: React.FC<OpenTradesTableProps> = ({
  trades = [],
  loading = false
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(trades.length / itemsPerPage);

  return (
    <div className={`mb-8 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="!text-base font-bold !text-slate-900 flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
          </span>
          Các giao dịch mở
        </h2>
        <select className="bg-white border border-slate-200 !text-slate-700 !text-xs font-medium rounded-md shadow-sm py-1.5 pl-3 pr-8 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer">
          <option value="all">Tất cả lệnh</option>
          <option value="buy">Chỉ lệnh Buy</option>
          <option value="sell">Chỉ lệnh Sell</option>
        </select>
      </div>
      <div className="bg-white rounded-xl shadow-premium border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr>
                {['Lệnh', 'Giờ vào lệnh', 'Khối lượng', 'Mã', 'PnL', 'Số pip', 'Thời lượng', ''].map((th, idx) => (
                  <th key={idx} scope="col" className={`px-6 py-4 !text-left !text-xs font-semibold !text-slate-500 ${idx >= 4 && idx <= 6 ? '!text-center' : ''}`}>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 bg-white">
              {
                trades.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 whitespace-nowrap !text-sm !text-slate-700 text-center">
                      Không có giao dịch mở nào
                    </td>
                  </tr>
                ) : (
                  trades.sort((a, b) => b.position_id - a.position_id).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((trade) => (
                    <TradeRow key={trade.id} trade={trade} />
                  ))
                )
              }
            </tbody>
          </table>
        </div>
      </div>
      <div className="w-full">
        {/* thanh phân trang & tổng số giao dịch */}
        <div className="flex flex-col sm:flex-row items-end sm:items-center justify-between gap-3 mt-3 pt-2 border-t border-slate-100 text-xs text-slate-500">
          <div>
            Tổng số giao dịch mở: <span className="text-slate-700 font-semibold">{trades.length}</span>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center justify-center p-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 disabled:hover:bg-slate-100 transition-colors"
              title="Trang trước"
            >
              <CaretLeftIcon size={14} weight="bold" />
            </button>

            <span className="px-2.5 py-1 rounded bg-slate-50 border border-slate-200 font-medium text-slate-700">
              {currentPage} / {totalPages || 1}
            </span>

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages || trades.length === 0}
              className="flex items-center justify-center p-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-40 disabled:hover:bg-slate-100 transition-colors"
              title="Trang sau"
            >
              <CaretRightIcon size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TradeRow: React.FC<{ trade: AccountLiveTrade }> = ({ trade }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  let pos: any = null;
  try {
    pos = typeof trade.position === 'string' ? JSON.parse(trade.position) : trade.position;
  } catch (e) {
    console.error('Error parsing trade position:', e);
  }

  if (!pos) return null;

  // Helper hàm parse thời gian UTC từ pos.entry_time
  const parseUtcDate = (raw: any): { dateObj: Date; timeMs: number } => {
    if (!raw) return { dateObj: new Date(NaN), timeMs: NaN };

    let timeMs: number;
    if (typeof raw === 'string') {
      const str = raw.trim();
      const isoStr = str.endsWith('Z') || str.includes('+')
        ? str
        : str.replace(' ', 'T') + 'Z';
      timeMs = new Date(isoStr).getTime();
    } else if (typeof raw === 'number') {
      timeMs = raw;
    } else {
      timeMs = new Date(raw).getTime();
    }

    return {
      dateObj: new Date(timeMs),
      timeMs
    };
  };

  // Dùng pos.entry_time từ bên trong object position
  const { dateObj: entryDate, timeMs: entryUtcMs } = parseUtcDate(pos.entry_time);

  return (
    <>
      <tr className="hover:bg-slate-50/50 transition-colors">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-col">
            <span className="!text-sm font-medium !text-slate-900 font-mono-num">#{pos.id}</span>
            <span className={`!text-xs font-semibold flex items-center gap-1 mt-0.5 ${pos.side?.toLowerCase() === 'buy' ? '!text-teal-500' : '!text-rose-500'}`}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {pos.side?.toLowerCase() === 'buy' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 19L19 5M19 5v10M19 5H9"></path>
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 19L5 5M5 5v10M5 5h10"></path>
                )}
              </svg>
              {pos.side}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap !text-sm !text-slate-700">
          <div className="flex flex-col">
            <span>{!isNaN(entryDate.getTime()) ? entryDate.toLocaleDateString('en-GB') : '--/--/----'}</span>
            <span>{!isNaN(entryDate.getTime()) ? entryDate.toLocaleTimeString('en-GB') : '--:--:--'}</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap !text-sm font-medium !text-slate-900 font-mono-num">{pos.volume_lots}</td>
        <td className="px-6 py-4 whitespace-nowrap !text-sm font-semibold !text-slate-900">{pos.symbol}</td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="bg-slate-50 py-1.5 px-4 rounded !text-center border border-slate-100">
            <span className={`!text-sm font-bold font-mono-num ${pos.profit >= 0 ? '!text-teal-500' : '!text-rose-500'}`}>
              {pos.profit >= 0 ? `+$${(pos.profit.toFixed(2))}` : `-$${(Math.abs(pos.profit).toFixed(2))}`}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="bg-slate-50 py-1.5 px-3 rounded !text-center border border-slate-100">
            <span className={`!text-sm font-medium font-mono-num ${pos.pips >= 0 ? '!text-teal-500' : '!text-rose-500'}`}>
              {pos.pips >= 0 ? `+${(pos.pips.toFixed(2))}` : `${(pos.pips.toFixed(2))}`}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="bg-slate-50 py-1.5 px-3 rounded !text-center border border-slate-100">
            <span className="!text-sm font-medium !text-slate-700 font-mono-num">
              {
                (() => {
                  if (isNaN(entryUtcMs)) return '--:--:--';

                  const nowUtcMs = Date.now();
                  const durationMs = Math.max(0, nowUtcMs - entryUtcMs);
                  const durationSec = Math.floor(durationMs / 1000);

                  const days = Math.floor(durationSec / 86400);
                  const hours = Math.floor((durationSec % 86400) / 3600);
                  const minutes = Math.floor((durationSec % 3600) / 60);
                  const seconds = durationSec % 60;

                  const pad = (n: number) => n.toString().padStart(2, '0');

                  if (days > 0) {
                    return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
                  }
                  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
                })()
              }
            </span>
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap !text-right">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`p-1.5 border border-slate-200 rounded-md !text-slate-500 hover:bg-slate-100 transition-all shadow-sm bg-white ${isExpanded ? 'bg-slate-100 rotate-180' : ''}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={8} className="px-6 py-4 bg-[#F4F4F4]">
            <TradeDetailsCard data={{
              type: pos.side,
              openPrice: pos.entry_price,
              closePrice: '-',
              currentBid: '0.00',
              currentAsk: '0.00',
              spread: '0',
              stopLoss: pos.stop_loss || '-',
              slPips: pos.sl_pips || '-',
              takeProfit: pos.take_profit || '-',
              tpPips: pos.tp_pips || '-',
              grossProfit: pos.profit.toFixed(2),
              swap: '?',
              commission: '?',
              netProfit: '?',
              openTime: pos.entry_time ? new Date(pos.entry_time).toLocaleString() : '-',
              closeTime: '-',
              maePip: '?',
              mae: '?',
              maeTime: '?',
              mfePip: '?',
              mfe: '?',
              mfeTime: '?',
            }} />
          </td>
        </tr>
      )}
    </>
  );
};

export default OpenTradesTable;